import React, { useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { SyncedPortalIcon, UnsyncedPortalIcon } from "assets/material-custom-icons";
import { AsyncContentMui } from "components";
import { useDevice, useFormatDateHook, useModals, useNavigate, useQueryHook } from "hooks";
import { useConnectionsSelect, useDataRoomsActions, useDateFormatSelect } from "store";
import { ConnectionPreview } from "store/types";

import { FreemiumMessages } from "../../../constants";
import { EmptyPortalIntegrations } from "./EmptyPortalIntegrations";

const getStatusColor = (isLoginValid: boolean | null, lastLoginError: string | null): string => {
  if (isLoginValid === null) return "#7b92a9"; // Loading/gray
  if (lastLoginError === "2FA_REQUIRED") return "#ebaf01"; // Warning/yellow
  if (isLoginValid) return "#07a27d"; // Success/green
  return "#ebaf01"; // Error/yellow
};

const getStatusIcon = (isLoginValid: boolean | null, lastLoginError: string | null) => {
  if (isLoginValid === null) return null; // Loading
  if (lastLoginError === "2FA_REQUIRED" || !isLoginValid) {
    return <UnsyncedPortalIcon sx={{ fontSize: "12px", color: "white" }} />;
  }
  return <SyncedPortalIcon sx={{ fontSize: "12px", color: "white" }} />;
};

export const PortalIntegrations: React.FC = () => {
  const connections = useConnectionsSelect();
  const { getConnectionsAction } = useDataRoomsActions();
  const { openDataroomConnect, infoModal } = useModals();
  const navigate = useNavigate();
  const formatDateHook = useFormatDateHook();
  const dateFormat = useDateFormatSelect();
  const { isMobile } = useDevice();

  const { loading } = useQueryHook(getConnectionsAction);

  const formatDate = (date?: string | number | Date | null | undefined) => {
    if (!date) return "-";
    return formatDateHook({ date, format: dateFormat });
  };

  const handleAddIntegration = () => {
    openDataroomConnect();
  };

  const handleOpenInNew = () => {
    isMobile &&
      infoModal({
        title: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_TITLE,
        msg: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_MESSAGE,
        buttonText: "Continue"
      });
    navigate({ route: "dataRooms" });
  };

  const tableRows = useMemo(() => {
    return connections.map((connection: ConnectionPreview) => {
      const statusColor = getStatusColor(connection.is_login_valid, connection.last_login_error);
      const statusIcon = getStatusIcon(connection.is_login_valid, connection.last_login_error);

      // Format updated data - this would need to come from the API or be calculated
      // For now, showing "-" if no sync data is available
      // In the future, this could show counts like "3 capital calls, 5 capital accounts, 2 distributions"

      return {
        id: connection.id,
        name: connection.type_title,
        lastUpdated: connection.last_connected || connection.last_sync || connection.last_index,
        statusColor,
        statusIcon,
        isLoginValid: connection.is_login_valid,
        lastLoginError: connection.last_login_error,
        updatedData: connection.files.updated_data
      };
    });
  }, [connections, formatDate]);

  return (
    <Stack
      sx={{
        width: "100%",
        p: "16px",
        gap: "16px",
        bgcolor: "white",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)"
      }}
      alignItems="stretch"
      direction="column"
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
        gap={isMobile ? "10px" : 0}
      >
        <Stack direction="row" alignItems="center" gap="10px">
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071f35" }}>Portal Integrations</Typography>
          <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={handleOpenInNew}>
            <OpenInNewIcon sx={{ width: "16px", height: "16px", color: "#071F35" }} />
          </IconButton>
        </Stack>

        <Button
          variant="outlined"
          startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          onClick={handleAddIntegration}
          sx={{
            minHeight: "24px",
            height: "24px",
            borderColor: "#7b92a9",
            color: "#071f35",
            fontSize: "12px",
            fontWeight: 400,
            textTransform: "none",
            borderRadius: "32px",
            px: "8px",
            py: "4px",
            minWidth: "auto",
            "&:hover": {
              borderColor: "#071f35",
              backgroundColor: "transparent"
            }
          }}
        >
          Add Integration
        </Button>
      </Stack>

      {/* Table */}
      <AsyncContentMui isLoading={loading}>
        {!tableRows.length ? (
          <EmptyPortalIntegrations onAddIntegration={handleAddIntegration} />
        ) : (
          <TableContainer
            sx={{
              maxHeight: "208px",
              overflow: "auto",
              borderRadius: "8px",
              border: "1px solid #DBE8F5"
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: isMobile ? "14px" : "16px",
                      fontWeight: 500,
                      color: "#071f35",
                      py: "8px",
                      px: isMobile ? "12px" : "20px",
                      width: isMobile ? "auto" : "260px",
                      minWidth: isMobile ? "150px" : "260px",
                      whiteSpace: "nowrap",
                      backgroundColor: "#F6F8FC"
                    }}
                  >
                    Dataroom
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: isMobile ? "14px" : "16px",
                      fontWeight: 500,
                      color: "#071f35",
                      py: "8px",
                      px: isMobile ? "12px" : "20px",
                      width: isMobile ? "auto" : "260px",
                      minWidth: isMobile ? "120px" : "260px",
                      whiteSpace: "nowrap",
                      backgroundColor: "#F6F8FC"
                    }}
                  >
                    Last Updated
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: isMobile ? "14px" : "16px",
                      fontWeight: 500,
                      color: "#071f35",
                      py: "8px",
                      px: isMobile ? "12px" : "20px",
                      whiteSpace: "nowrap",
                      backgroundColor: "#F6F8FC"
                    }}
                  >
                    Updated Data
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      borderBottom: index === tableRows.length - 1 ? "none" : "1px solid #DBE8F5"
                    }}
                  >
                    <TableCell
                      sx={{
                        py: "12px",
                        px: isMobile ? "12px" : "20px",
                        borderBottom: index === tableRows.length - 1 ? "none" : "1px solid #DBE8F5"
                      }}
                    >
                      <Chip
                        icon={
                          <Box
                            sx={{
                              width: "22px",
                              height: "22px",
                              borderRadius: "20px",
                              backgroundColor: row.statusColor,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            {row.statusIcon}
                          </Box>
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: isMobile ? "14px" : "16px",
                              fontWeight: 700,
                              color: "#071f35",
                              ml: "6px"
                            }}
                          >
                            {row.name}
                          </Typography>
                        }
                        sx={{
                          backgroundColor: "#DBE8F5",
                          height: "auto",
                          py: "5px",
                          px: "4px",
                          "& .MuiChip-icon": {
                            margin: 0
                          },
                          "& .MuiChip-label": {
                            padding: 0
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        py: "12px",
                        px: isMobile ? "12px" : "20px",
                        borderBottom: index === tableRows.length - 1 ? "none" : "1px solid #DBE8F5",
                        fontSize: isMobile ? "14px" : "16px",
                        color: "#071f35"
                      }}
                    >
                      {formatDate(row.lastUpdated)}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: "12px",
                        px: isMobile ? "12px" : "20px",
                        borderBottom: index === tableRows.length - 1 ? "none" : "1px solid #DBE8F5",
                        fontSize: isMobile ? "14px" : "16px",
                        color: "#071f35"
                      }}
                    >
                      {row.updatedData
                        ? Object.entries(row.updatedData)
                            .map(([key, value]) => `${value} ${key}`)
                            .join(", ")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AsyncContentMui>
    </Stack>
  );
};
